import { TAdFoxBannerCompleteSizes } from '@cian/adfox-component';
import { MortgageAnnotations, ScrollFade } from '@cian/adfox-templates';
import { useBooleanState } from '@cian/react-utils';
import {
  Avatar,
  Button,
  IconButton,
  Image,
  Label,
  Overline,
  UIHeading2,
  UIHeading4,
  UIText1,
  UIText2,
  UIText3,
} from '@cian/ui-kit';
import { IconActionClose16 } from '@cian/ui-kit-design-tokens/icons';

import cn from 'classnames';
import { FC, MouseEvent as ReactMouseEvent, useCallback } from 'react';

import { Transport, TransportProps } from './internal/components/Transport';

import * as styles from './TgbNativedescGeneral.css';

type TransportIterable = TransportProps & {
  id: number;
};

export type TgbNativedescGeneralProps = {
  sizes: TAdFoxBannerCompleteSizes;
  url: string;
  image: string;
  logoImageUrl: string | undefined;
  title: string;
  description: string;
  address: string;
  builderName: string;
  annotation: string;
  button: string | undefined;
  label: string | undefined;
  transports: TransportIterable[];
  mortgageWarning: string | undefined;
  mortgageTermsWarning: string | undefined;
};

export const TgbNativedescGeneral: FC<TgbNativedescGeneralProps> = props => {
  const {
    title,
    address,
    description,
    annotation,
    image,
    transports,
    url,
    button,
    label,
    logoImageUrl,
    builderName,
    sizes,
    mortgageWarning,
    mortgageTermsWarning,
  } = props;

  const { state: isClosed, setTrue: setClosed } = useBooleanState();

  const handleClose = useCallback<(event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void>(
    event => {
      event.preventDefault();

      setClosed();
    },
    [setClosed],
  );

  if (isClosed) {
    return <div className={styles['empty']} data-testid="empty_native_adfox_block" />;
  }

  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  return (
    <a
      className={cn(styles['container'], withMortgageAnnotations && styles['container--mortgage-annotations'])}
      href={url}
      rel="noopener noreferrer"
      style={sizes}
      target="_blank"
    >
      <div className={styles['content']}>
        <div className={styles['photo-block']}>
          <Image objectFit="cover" src={image} />
        </div>
        <div className={styles['main-content-block']}>
          <div className={styles['content-container']}>
            <div>
              <div className={styles['title-wrapper']}>
                <UIHeading2 as="h2" color="text-main-default">
                  {title}
                </UIHeading2>
              </div>
              <div className={styles['subtitle-wrapper']}>
                <UIHeading4 as="h4">{description}</UIHeading4>
              </div>

              <div className={styles['main-content']}>
                {!!transports.length && (
                  <div className={styles['transports-container']} data-testid="transports">
                    {transports.map(transport => (
                      <Transport
                        iconColor={transport.iconColor}
                        iconType={transport.iconType}
                        key={transport.id}
                        name={transport.name}
                        remoteness={transport.remoteness}
                      />
                    ))}
                  </div>
                )}

                <UIText2 color="gray60_100" data-testid="address">
                  {address}
                </UIText2>
              </div>
            </div>
          </div>
          <div className={styles['footer-container']}>
            <UIText3 color="gray40_100">{annotation}</UIText3>
            <ScrollFade size={12} />
          </div>
        </div>
        <div className={styles['publisher-block']}>
          <div className={styles['publisher-block-content']}>
            <div className={styles['publisher-avatar-wrapper']}>
              <Avatar size={64} src={logoImageUrl} type="builder" />
            </div>

            <Overline color="gray60_100">Компания</Overline>

            <UIText1 data-testid="builderName" fontWeight="bold">
              {builderName}
            </UIText1>

            <div className={styles['more-info-button-wrapper']}>
              <Button fullWidth data-testid="more-info-button" size="XS">
                {button || 'Узнать подробности'}
              </Button>
            </div>
          </div>

          {!!label && (
            <div>
              <Label background="control-main-secondary-default" color="decorative-theme-dark">
                {label}
              </Label>
            </div>
          )}
        </div>
        <div className={styles['close-block']} data-testid="close-block">
          <div>
            <IconButton data-testid="close-button" icon={<IconActionClose16 />} onClick={handleClose} />
          </div>
        </div>
      </div>

      {withMortgageAnnotations && (
        <div className={styles['mortgage-annotations']}>
          <MortgageAnnotations
            height={sizes.height}
            mortgageTermsWarning={mortgageTermsWarning}
            mortgageWarning={mortgageWarning}
          />
        </div>
      )}
    </a>
  );
};
